var _dec, _dec2, _dec3, _dec4, _class, _DrawerLayout;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component, createContext } from 'react';
import { textDirectionContextConsumer } from '@instructure/ui-i18n';
import { matchComponentTypes, safeCloneElement, withDeterministicId } from '@instructure/ui-react-utils';
import { getBoundingClientRect } from '@instructure/ui-dom-utils';
import { createChainedFunction, px } from '@instructure/ui-utils';
import { logError as error } from '@instructure/console';
import { testable } from '@instructure/ui-testable';
import { mirrorHorizontalPlacement } from '@instructure/ui-position';
import { DrawerContent } from './DrawerContent';
import { DrawerTray } from './DrawerTray';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let DrawerLayout = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, null), _dec3 = textDirectionContextConsumer(), _dec4 = testable(), _dec(_class = _dec2(_class = _dec3(_class = _dec4(_class = (_DrawerLayout = class DrawerLayout extends Component {
  constructor(_props) {
    super(_props);
    this._id = void 0;
    this._content = null;
    this._tray = null;
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleTrayContentRef = el => {
      this._tray = el;
    };
    this.handleContentSizeChange = ({
      width
    }) => {
      this.setState((state, props) => {
        const nextState = this.getNextState(props.minWidth, state.trayWidth, width, state.shouldOverlayTray);
        if (state.shouldOverlayTray !== nextState.shouldOverlayTray) {
          this.notifyOverlayTrayChange(nextState.shouldOverlayTray);
        }
        return nextState;
      });
    };
    this.handleTraySizeChange = ({
      width
    }) => {
      this.setState((state, props) => {
        const nextState = this.getNextState(props.minWidth, width, state.contentWidth, true);
        if (state.shouldOverlayTray !== nextState.shouldOverlayTray) {
          this.notifyOverlayTrayChange(nextState.shouldOverlayTray);
        }
        return nextState;
      });
    };
    this.handleTrayTransitionEnter = () => {
      let width = 0;
      if (this._tray) {
        width = getBoundingClientRect(this._tray).width;
      }
      this.handleTraySizeChange({
        width
      });
    };
    this.handleTrayTransitionExit = () => {
      this.handleTraySizeChange({
        width: 0
      });
    };
    this.state = {
      shouldOverlayTray: false,
      trayWidth: 0,
      contentWidth: 0
    };
    this._id = _props.deterministicId();
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  get trayProps() {
    const tray = Children.toArray(this.props.children).filter(child => matchComponentTypes(child, [DrawerTray]))[0];
    return tray.props;
  }
  get trayPlacement() {
    const _this$trayProps = this.trayProps,
      placement = _this$trayProps.placement,
      _this$trayProps$dir = _this$trayProps.dir,
      dir = _this$trayProps$dir === void 0 ? this.props.dir : _this$trayProps$dir;
    const isRtl = dir === textDirectionContextConsumer.DIRECTION.rtl;
    return isRtl ? mirrorHorizontalPlacement(placement, ' ') : placement;
  }
  get contentMargin() {
    const trayWidth = this.state.trayWidth || 0;
    return this.state.shouldOverlayTray ? 0 : trayWidth;
  }
  get contentStyle() {
    const shouldOverlayTray = this.shouldOverlayTray(this.props.minWidth, this.state.trayWidth, this.state.contentWidth, this.state.shouldOverlayTray);
    let marginLeft = 0;
    let marginRight = 0;
    if (!shouldOverlayTray) {
      if (this.trayPlacement === 'start') {
        marginLeft = this.contentMargin;
      }
      if (this.trayPlacement === 'end') {
        marginRight = this.contentMargin;
      }
    }
    return {
      marginLeft: `${marginLeft}px`,
      marginRight: `${marginRight}px`
    };
  }
  shouldOverlayTray(minWidth, trayWidth, contentWidth, trayIsOverlayed) {
    if (!this._content) return false;
    const minWidthPx = px(minWidth, this._content);
    if (trayIsOverlayed) {
      return contentWidth - trayWidth < minWidthPx;
    } else {
      return contentWidth < minWidthPx;
    }
  }
  getNextState(minWidth, trayWidth, contentWidth, trayIsOverlayed) {
    const shouldOverlayTray = this.shouldOverlayTray(minWidth, trayWidth, contentWidth, trayIsOverlayed);
    return {
      trayWidth,
      contentWidth,
      shouldOverlayTray
    };
  }
  notifyOverlayTrayChange(shouldOverlayTray) {
    const onOverlayTrayChange = this.props.onOverlayTrayChange;
    if (typeof onOverlayTrayChange === 'function') {
      onOverlayTrayChange(shouldOverlayTray);
    }
  }
  renderChildren() {
    let trayCount = 0;
    let contentCount = 0;
    const children = Children.map(this.props.children, (child, _index) => {
      if (matchComponentTypes(child, [DrawerTray])) {
        trayCount++;
        return safeCloneElement(child, {
          label: child.props.label,
          key: child.props.label,
          [DrawerTray.locatorAttribute]: this._id,
          contentRef: this.handleTrayContentRef,
          onEnter: this.handleTrayTransitionEnter,
          onExit: this.handleTrayTransitionExit
        });
      } else if (matchComponentTypes(child, [DrawerContent])) {
        contentCount++;
        const handleContentRef = el => {
          const contentRef = child.props.contentRef;
          this._content = el;
          if (typeof contentRef === 'function') {
            contentRef(el);
          }
        };
        return this.state.trayWidth !== null ? safeCloneElement(child, {
          label: child.props.label,
          key: child.props.label,
          [DrawerContent.locatorAttribute]: this._id,
          style: this.contentStyle,
          onSizeChange: createChainedFunction(this.handleContentSizeChange, child.props.onSizeChange),
          contentRef: handleContentRef
        }) : null;
      } else {
        return child;
      }
    });
    error(trayCount <= 1, `[DrawerLayout] Only one 'DrawerTray' per 'DrawerLayout' is supported.`);
    error(contentCount <= 1, `[DrawerLayout] Only one 'DrawerContent' per 'DrawerLayout' is supported.`);
    return children;
  }
  render() {
    var _this$props$styles;
    const props = {
      [DrawerLayout.locatorAttribute]: this._id
    };
    return _jsx(DrawerLayoutContext.Provider, {
      value: this.state.shouldOverlayTray,
      children: _jsx("div", {
        ...props,
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.drawerLayout,
        ref: this.handleRef,
        children: this.renderChildren()
      })
    });
  }
}, _DrawerLayout.displayName = "DrawerLayout", _DrawerLayout.componentId = 'DrawerLayout', _DrawerLayout.locatorAttribute = 'data-drawer-layout', _DrawerLayout.propTypes = propTypes, _DrawerLayout.allowedProps = allowedProps, _DrawerLayout.defaultProps = {
  minWidth: '30rem'
}, _DrawerLayout.Content = DrawerContent, _DrawerLayout.Tray = DrawerTray, _DrawerLayout)) || _class) || _class) || _class) || _class);
export default DrawerLayout;
export { DrawerLayout, DrawerContent, DrawerTray };
export const DrawerLayoutContext = /*#__PURE__*/createContext(false);